/* cali consent — in-house cookie consent UI for labomono.com
 *
 * Shopify's Customer Privacy API remains the source of truth: it stores the
 * decision, propagates it to checkout, and gates every Shopify-managed pixel
 * (Google/GA4 channel pixel, Meta, TikTok, Klaviyo). This file only draws the
 * UI, reports the decision back to Shopify, and mirrors it into Google Consent
 * Mode for the theme-hardcoded GTM container.
 *
 * Deliberate constraints:
 *  - Accept and Decline are equally prominent (CNIL/EDPB). Do not "optimise"
 *    that away; the compliant CRO levers are copy length and bar size.
 *  - Nothing is assumed granted. If the API fails to load we show nothing and
 *    leave Shopify's own defaults untouched.
 */
(function () {
  'use strict';

  var CATEGORIES = ['analytics', 'marketing', 'preferences', 'sale_of_data'];

  var root = document.querySelector('[data-cali-consent]');
  if (!root) return;

  var configEl = document.getElementById('CaliConsentConfig');
  var config = {};
  try {
    config = JSON.parse(configEl.textContent);
  } catch (err) {
    config = {};
  }

  var bar = root.querySelector('[data-cali-consent-bar]');
  var sheet = root.querySelector('[data-cali-consent-sheet]');
  var panel = root.querySelector('[data-cali-consent-panel]');
  var lastFocus = null;
  var apiReady = false;
  var shownLogged = false;

  /* ------------------------------------------------------------------ *
   * Google Consent Mode mirror (for the theme-hardcoded GTM container)
   * ------------------------------------------------------------------ */

  function gtagConsentUpdate(consent) {
    if (!config.gateGtm) return;
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    gtag('consent', 'update', {
      ad_storage: consent.marketing ? 'granted' : 'denied',
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.marketing ? 'granted' : 'denied',
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      personalization_storage: consent.preferences ? 'granted' : 'denied',
      functionality_storage: consent.preferences ? 'granted' : 'denied'
    });
    window.dataLayer.push({ event: 'cali_consent_update' });
  }

  /* ------------------------------------------------------------------ *
   * Beacon — makes consent rate a measurable KPI
   * ------------------------------------------------------------------ */

  function report(action, consent) {
    if (!config.beaconUrl) return;
    var payload = {
      // Matches the shape /api/client-beacon expects: a whitelisted `type`
      // plus arbitrary fields. Sent as text/plain to skip the CORS preflight,
      // same convention as cart-validation.js.
      type: 'consent_' + action,
      region: safeRegion(),
      page: window.location.pathname.slice(0, 60),
      // A width bucket rather than the raw value: enough to compare mobile and
      // desktop accept rate, without collecting a fingerprintable dimension.
      viewport: window.innerWidth < 750 ? 'mobile' : 'desktop',
      consent: consent || null
    };
    try {
      var blob = new Blob([JSON.stringify(payload)], { type: 'text/plain' });
      if (navigator.sendBeacon && navigator.sendBeacon(config.beaconUrl, blob)) return;
    } catch (err) { /* fall through */ }
    try {
      fetch(config.beaconUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload),
        keepalive: true,
        mode: 'cors'
      }).catch(function () {});
    } catch (err) { /* never let reporting break the banner */ }
  }

  function safeRegion() {
    try {
      return window.Shopify.customerPrivacy.getRegion() || '';
    } catch (err) {
      return '';
    }
  }

  /* ------------------------------------------------------------------ *
   * Writing consent back to Shopify
   * ------------------------------------------------------------------ */

  function applyConsent(consent, action) {
    if (!apiReady) return;
    try {
      window.Shopify.customerPrivacy.setTrackingConsent(
        {
          analytics: !!consent.analytics,
          marketing: !!consent.marketing,
          preferences: !!consent.preferences,
          sale_of_data: !!consent.sale_of_data
        },
        function () {
          gtagConsentUpdate(consent);
          report(action, consent);
        }
      );
    } catch (err) {
      // Shopify rejected the call: leave the banner up rather than pretend a
      // decision was recorded.
      return;
    }
    hideBar();
    closeSheet();
  }

  function allOf(value) {
    var out = {};
    CATEGORIES.forEach(function (key) { out[key] = value; });
    return out;
  }

  function readSheet() {
    var out = {};
    CATEGORIES.forEach(function (key) {
      var input = panel.querySelector('input[data-cali-consent-category="' + key + '"]');
      out[key] = input ? input.checked : false;
    });
    return out;
  }

  function writeSheet(consent) {
    CATEGORIES.forEach(function (key) {
      var input = panel.querySelector('input[data-cali-consent-category="' + key + '"]');
      if (input) input.checked = !!consent[key];
    });
  }

  /* Effective permission, not the raw stored string. currentVisitorConsent()
     returns '' for visitors in regions with no opt-in requirement, who are
     nevertheless allowed to be tracked — reading the raw value there would
     wrongly report "denied" and switch their tracking off. The *Allowed()
     methods answer the question we actually care about. */
  function currentConsent() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp) return allOf(false);
    try {
      return {
        analytics: cp.analyticsProcessingAllowed(),
        marketing: cp.marketingAllowed(),
        preferences: cp.preferencesProcessingAllowed(),
        sale_of_data: cp.saleOfDataAllowed()
      };
    } catch (err) {
      return allOf(false);
    }
  }

  /* Whether this visitor still owes us a decision.

     NOT shouldShowBanner(): verified on 2026-08-17 that it keeps returning
     true even after consent has been recorded and analyticsProcessingAllowed()
     has flipped to true, which would show the bar on every page load forever.
     The regulation-specific checks do flip correctly. */
  function bannerRequired() {
    var cp = window.Shopify.customerPrivacy;
    var gdpr = typeof cp.shouldShowGDPRBanner === 'function' ? cp.shouldShowGDPRBanner() : null;
    var ccpa = typeof cp.shouldShowCCPABanner === 'function' ? cp.shouldShowCCPABanner() : null;
    if (gdpr === null && ccpa === null) {
      try {
        return cp.shouldShowBanner();
      } catch (err) {
        return false;
      }
    }
    return !!gdpr || !!ccpa;
  }

  /* ------------------------------------------------------------------ *
   * Show / hide
   * ------------------------------------------------------------------ */

  function showBar() {
    if (!bar) return;
    root.hidden = false;
    bar.hidden = false;
    if (!shownLogged) {
      shownLogged = true;
      report('shown', null);
    }
  }

  function hideBar() {
    if (!bar) return;
    bar.hidden = true;
    if (!sheet || sheet.hidden) root.hidden = true;
  }

  function openSheet() {
    if (!sheet) return;
    lastFocus = document.activeElement;
    writeSheet(currentConsent());
    root.hidden = false;
    sheet.hidden = false;
    document.addEventListener('keydown', onKeydown, true);
    var first = panel.querySelector('input:not(:disabled), button');
    if (first) first.focus();
  }

  function closeSheet() {
    if (!sheet || sheet.hidden) return;
    sheet.hidden = true;
    document.removeEventListener('keydown', onKeydown, true);
    if (bar && bar.hidden) root.hidden = true;
    if (lastFocus && document.contains(lastFocus)) {
      lastFocus.focus();
      lastFocus = null;
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeSheet();
      return;
    }
    if (event.key !== 'Tab') return;
    // Focus trap: the sheet is modal, so Tab must not escape behind it.
    var focusables = panel.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled)');
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */

  root.addEventListener('click', function (event) {
    var action = event.target.closest('[data-cali-consent-action]');
    if (!action) {
      // Backdrop click closes the sheet without recording a decision.
      if (event.target === sheet) closeSheet();
      return;
    }
    event.preventDefault();
    switch (action.getAttribute('data-cali-consent-action')) {
      case 'accept':
        applyConsent(allOf(true), 'accept');
        break;
      case 'decline':
        applyConsent(allOf(false), 'decline');
        break;
      case 'open':
        openSheet();
        break;
      case 'close':
        closeSheet();
        break;
      case 'save':
        applyConsent(readSheet(), 'save');
        break;
    }
  });

  // Withdrawing consent must be as easy as giving it. Any link pointing at
  // #cookie-preferences (e.g. a footer menu item) reopens the sheet.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('a[href$="#cookie-preferences"], [data-cali-consent-open]');
    if (!trigger) return;
    event.preventDefault();
    openSheet();
  });

  window.caliConsent = {
    openPreferences: openSheet,
    current: currentConsent
  };

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  function boot() {
    apiReady = true;
    var shouldShow = false;
    try {
      shouldShow = bannerRequired();
    } catch (err) {
      shouldShow = false;
    }

    // Mirror the visitor's effective permission into Consent Mode on every page
    // load, otherwise GTM sits at the denied default for someone who already
    // accepted, or for a visitor in a region that never needed a banner.
    if (!shouldShow) gtagConsentUpdate(currentConsent());

    if (shouldShow) showBar();
  }

  if (window.Shopify && window.Shopify.customerPrivacy && window.Shopify.customerPrivacy.shouldShowBanner) {
    boot();
  } else if (window.Shopify && window.Shopify.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
      if (error) return; // No API: show nothing rather than a banner that can't record.
      boot();
    });
  }

  document.addEventListener('visitorConsentCollected', function (event) {
    var detail = event.detail || {};
    gtagConsentUpdate({
      analytics: detail.analyticsAllowed,
      marketing: detail.marketingAllowed,
      preferences: detail.preferencesAllowed,
      sale_of_data: detail.saleOfDataAllowed
    });
    hideBar();
  });
})();
